import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ART } from "../assets";

export default function Hero({ onStart }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  // Jetson-style: the full-bleed illustration gently shrinks into a rounded card on scroll
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.88]);
  const radius = useTransform(scrollYProgress, [0, 1], [0, 56]);
  const overlay = useTransform(scrollYProgress, [0, 0.6], [1, 0.35]);

  return (
    <section ref={ref} id="top" className="relative h-[100svh] min-h-[640px] w-full overflow-hidden">
      {/* Full-bleed illustration — the entire hero */}
      <motion.div
        style={{ scale, borderRadius: radius }}
        className="absolute inset-0 overflow-hidden will-change-transform"
      >
        <img
          src={ART.hero}
          alt="A milla crew clearing a home's garage at the curb"
          className="h-full w-full object-cover"
        />
        {/* soft scrim concentrated behind the headline; keeps the truck vivid below */}
        <motion.div
          style={{ opacity: overlay }}
          className="absolute inset-x-0 top-0 h-[55%] bg-gradient-to-b from-canvas via-canvas/55 to-transparent"
        />
      </motion.div>

      {/* Centered content overlaid on the illustration */}
      <div className="relative z-10 mx-auto flex h-full max-w-5xl flex-col items-center px-5 pt-[9vh] text-center sm:pt-[10vh]">
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 max-w-4xl font-display text-[3.6rem] leading-[0.92] tracking-tight text-charcoal sm:text-7xl lg:text-[6.2rem]"
        >
          Get your <span className="italic text-violet">space</span> back.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
          style={{ textShadow: "0 1px 18px rgba(247,245,239,0.95), 0 1px 4px rgba(247,245,239,0.95)" }}
          className="mt-5 max-w-2xl text-lg font-semibold leading-snug text-charcoal"
        >
          Snap a photo — we handle the lifting, hauling, and donating. What's
          left is a room that feels like yours again.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.28, ease: [0.16, 1, 0.3, 1] }}
          className="mt-7 flex flex-wrap items-center justify-center gap-4"
        >
          <button
            onClick={onStart}
            data-testid="hero-start-cta"
            className="group inline-flex items-center gap-3 rounded-full bg-violet px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-violet-deep hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-12px_rgba(76,0,140,0.55)]"
          >
            Get an instant quote
            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </button>
        </motion.div>
      </div>
    </section>
  );
}
